import { Inject } from '@angular/core';
import { AfterViewInit, ChangeDetectorRef, Component, effect, Input, OnInit } from '@angular/core';
import { SESSION_DATA_SOURCE, SessionDataSource } from '../../../core/services/session-data-source';
import { AlarmOption, PatientTargetDataModel, PatientTargetModel, PatientTargetStatus } from '../../../core/api/models';
import { BaseBoxComponent } from '../base-box/base-box.component';
import { AlertService } from '../../../shared/services/alert.service';
@Component({
  selector: 'app-box-interval',
  templateUrl: './box-interval.component.html',
    standalone: false,
})
export class BoxIntervalComponent extends BaseBoxComponent implements AfterViewInit, OnInit {
  text: string | undefined = '-';
   expectedAmount: number = 0;
  @Input() isTopBox: boolean = true;
  @Input() isBottomBox: boolean = false;
  @Input() target: PatientTargetModel | undefined = undefined;
  @Input() isPreview?: boolean;
  timerInitialValue: number = 60;
  targetID: number = 0;
  targetData: PatientTargetDataModel[] = [];

  isStarted: boolean = false;
  timerValue: number = 0;
  private startTime: number = 0;
  private lastCompletedStartTime: number = 0;
  intervals: { happened: boolean; RecordedDate: string; FormattedDate: string;}[] = [];  
  happened: boolean = false;

  timeOut: any;
  alarmTimeout: any;
  remainingTrials: number = 0;
  constructor(
    protected override cdRef: ChangeDetectorRef,
    @Inject(SESSION_DATA_SOURCE) private patientSessionService: SessionDataSource,
    private alertService: AlertService
  ) {
    super(cdRef);
    effect(() => {
      this.patientSessionService.dataChanged();
      const targetData = this.patientSessionService.getTargetData(this.targetID);
      if (targetData) {
        this.targetData = targetData.filter(t => !t.DeletedDate);
        
        this.remainingTrials = (this.target?.DesiredTrials ?? 0) - this.targetData.length;
        if (this.remainingTrials < 0) {
          this.remainingTrials = 0;
        }

        if (this.target?.TargetStatus === PatientTargetStatus.Mastered || this.target?.TargetStatus === PatientTargetStatus.Closed || this.target?.TargetStatus === PatientTargetStatus.Fluent) {
          this.remainingTrials = 0;
        }
        this.intervals = this.targetData.map(t => {
          if (!t.TextValue) {
            t.TextValue = '';
          }
          const happened = (t.IntValue ?? 0) > 0;
          return {
            happened,
            RecordedDate: t.RecordedDate ?? '',
            FormattedDate: this.formatDate(new Date(t.RecordedDate ?? '')),
          };

        });

      }
      else {
        this.targetData = [];
        this.intervals = [];
      }
    });

    effect(() => {
      this.patientSessionService.dataChanged();
      this.syncFromRunningData();
    });
  }

  ngOnInit() {
    this.text = this.target?.TargetName ?? '-';
    this.targetID = this.target?.ID ?? 0;
    this.expectedAmount = this.target?.DesiredTrials ?? 1;
    this.timerInitialValue = this.target?.Duration ?? 60;
    this.timerValue = this.timerInitialValue;
    this.syncFromRunningData();
  }

  override doLongPress(): void {
    this.showExtra2();
  }

  startTimer() {
    const running = this.patientSessionService.getTargetsRunningData(
      this.getSessionID(),
      this.targetID
    );
    const sharedStartTime = running?.Data?.startTime ?? 0;
    const sharedStillRunning =
      !!running?.Data?.isStarted &&
      sharedStartTime > 0 &&
      this.remainingSeconds(sharedStartTime) > 0;

    if (this.isStarted || sharedStillRunning) {
      if (!this.isStarted && sharedStillRunning) {
        this.syncFromRunningData();
      }
      this.showExtra();
      return;
    }

    this.isStarted = true;
    this.startTime = Date.now();
    this.happened = false;
    this.saveTargetRunningData();
    this.updateTimer();
  }


  saveTargetRunningData() {
    this.patientSessionService.setTargetsRunningData(this.getSessionID(), this.targetID, {
      isIntervalTarget: true,
      isStarted: this.isStarted,
      startTime: this.startTime,
      timerInitialValue: this.timerInitialValue,
      timerValue: this.timerValue,
      happened: this.happened,
      lastCompletedStartTime: this.lastCompletedStartTime,
    });
  }

  private updateTimer(): void {
    if (this.isStarted) {
      this.timerValue = this.getCurrentTimerValue();

      if (this.timerValue <= 0) {
        const shouldRingAlarm = this.target?.AlarmOption === AlarmOption.AlarmAtEnd;
        this.ringAlarmIfNeeded(
          this.target?.AlarmOption ?? AlarmOption.NoAlarm,
          this.timerValue,
          this.target?.Duration ?? 0,
          this.target?.AlarmSoundOption ?? 1,
          false
        );

        if (this.alarmTimeout) {
          return;
        }
        if (shouldRingAlarm) {
          this.alarmTimeout = setTimeout(() => {
            this.completeInterval();
          }, 100);
        } else {
          this.completeInterval();
        }
        return;
      }

      this.ringAlarmIfNeeded(
        this.target?.AlarmOption ?? AlarmOption.NoAlarm,
        this.timerValue,
        this.target?.Duration ?? 0,
        this.target?.AlarmSoundOption ?? 1,
        false
      );

      if (this.timeOut) {
        clearTimeout(this.timeOut);
      }
      this.timeOut = setTimeout(() => {
        this.updateTimer();
      }, 1000);
    }else {
      this.clearTimerTimeouts();
    }
  }

  private getCurrentTimerValue(): number {
    return this.remainingSeconds(this.startTime);
  }

  private remainingSeconds(startTime: number): number {
    if (!startTime) {
      return this.timerInitialValue;
    }
    return Math.max(
      0,
      this.timerInitialValue - Math.floor((Date.now() - startTime) / 1000)
    );
  }

  private syncFromRunningData() {
    const targetsRunningData = this.patientSessionService.getTargetsRunningData(
      this.getSessionID(),
      this.targetID
    );
    if (targetsRunningData) {
      const wasStarted = this.isStarted;
      this.isStarted = targetsRunningData.Data.isStarted;
      this.startTime = targetsRunningData.Data.startTime;
      this.lastCompletedStartTime =
        targetsRunningData.Data.lastCompletedStartTime ?? 0;
      this.timerValue = this.isStarted
        ? this.getCurrentTimerValue()
        : targetsRunningData.Data.timerValue ?? this.timerInitialValue;
      this.happened = targetsRunningData.Data.happened ?? false;
      if (wasStarted && !this.isStarted &&
          this.lastCompletedStartTime === this.startTime &&
          this.getCurrentTimerValue() <= 0 && !this.isAlarmRinging) {
        this.ringAlarmIfNeeded(
          this.target?.AlarmOption ?? AlarmOption.NoAlarm,
          0,
          this.target?.Duration ?? 0,
          this.target?.AlarmSoundOption ?? 1,
          false
        );
      }
      if (this.isStarted && !wasStarted) {
        if (this.getCurrentTimerValue() <= 0) {
          this.completeInterval();
          return;
        }
        this.clearTimerTimeouts();
        this.timeOut = setTimeout(() => {
          this.updateTimer();
        }, 10);
      }
    } else {
      this.isStarted = false;
      this.startTime = 0;
      this.timerValue = this.timerInitialValue;
      this.happened = false;
    }
  }

  override ngOnDestroy() {
    this.clearTimerTimeouts();
    super.ngOnDestroy();
  }

  completeInterval() {
    this.clearTimerTimeouts();
    const startTime = this.startTime;
    if (!this.patientSessionService.completeInterval(this.getSessionID(), this.targetID, startTime)) {
      this.resetLocalTimerState();
      this.closeExtra();
      return;
    }

    this.lastCompletedStartTime = startTime;
    this.resetLocalTimerState();
    this.saveTargetRunningData();
    this.closeExtra();
  }

  cancelTimer() {
    this.completeInterval();
  }

  timerString() {
    const minutes = Math.floor(this.timerValue / 60);
    const seconds = this.timerValue % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  lastInterval() {
    if (this.intervals.length > 0) {
      const lastInterval = this.intervals[this.intervals.length - 1];
      return `${lastInterval.happened ? 'Happened' : "Didn't happen"}`;
    } else {
      return '';
    }
  }

  setHappened(event: any) {
    this.happened = event.checked;
    this.saveTargetRunningData();
  }

  deleteData(data: any) {
    //confirm the deletion
    const confirmed = this.alertService.confirm('Are you sure you want to delete this data?', 'Delete Data');
    confirmed.subscribe(result => {
      if (result) {
        let targetDataToSave: PatientTargetDataModel | undefined = this.targetData.find(t =>
          t.RecordedDate === data.RecordedDate
        );

        if (targetDataToSave) {
          targetDataToSave.DeletedDate = new Date().toServerString();
          this.patientSessionService.saveTargetData(targetDataToSave);
        }
      }
    });
  }

  private getSessionID(): number {
    return this.patientSessionService.getPatientSession()?.ID ?? -1;
  }

  private resetLocalTimerState() {
    this.isStarted = false;
    this.timerValue = this.timerInitialValue;
    this.happened = false;
    this.startTime = 0;
  }

  private clearTimerTimeouts() {
    if (this.timeOut) {
      clearTimeout(this.timeOut);
      this.timeOut = null;
    }
    if (this.alarmTimeout) {
      clearTimeout(this.alarmTimeout);
      this.alarmTimeout = null;
    }
  }
}
